<template>
    <div class="form-feed">
        <div class="gcmcn-form-title">{{ feed === null ? 'Add' : 'Update' }} feed</div>
        <el-alert
            v-if="feed !== null && feed.status === 'FINISHED'"
            title="Editing a finished feed is not allowed"
            type="warning"
            effect="dark"
            :closable="false"
            style="margin-bottom: 20px;"
        >
        </el-alert>
        <div v-if="formInitialised" class="gcmcn-card form-feed">
            <el-form :model="form" :rules="rules" ref="form">
                <div class="gcmcn-inputs-container">
                    <div class="flex-basis-6">
                        <el-form-item class="gcmcn-form-input">
                            <div class="gcmcn-form-label">Source</div>
                            <el-switch
                                v-model="externalSource"
                                :disabled="feed !== null"
                                active-text="Venue"
                                inactive-text="Globecast"
                            >
                            </el-switch>
                        </el-form-item>
                    </div>
                </div>
                <div class="gcmcn-inputs-container">
                    <div class="flex-basis-6">
                        <el-form-item class="gcmcn-form-input" prop="display_name">
                            <div class="gcmcn-form-label">Name</div>
                            <el-input
                                size="mini"
                                v-model="form.display_name"
                            ></el-input>
                        </el-form-item>
                    </div>
                    <div class="flex-basis-3">
                        <upload-file
                            style="margin-top: 20px;"
                            text="Upload thumbnails"
                            :default-image="defaultImage"
                            :disabled="feed !== null && feed.status === 'FINISHED'"
                            @image-uploaded="onCropImage"
                        ></upload-file>
                    </div>
                </div>
                <div class="gcmcn-inputs-container">
                    <div class="flex-basis-10">
                        <el-form-item class="gcmcn-form-input" prop="event_uid">
                            <div class="gcmcn-form-label">Event</div>
                            <el-select
                                v-model="form.event_uid"
                                popper-class="gcmcn-popper"
                                size="mini"
                                :disabled="cantEditFeed"
                                placeholder="Select an event"
                                @change="eventChanged"
                            >
                                <el-option
                                    v-for="event in availableEvents"
                                    :key="event.uid"
                                    :label="event.name"
                                    :value="event.uid"
                                ></el-option>
                            </el-select>
                        </el-form-item>
                    </div>
                </div>
                <div
                    v-if="(userIsAtLeastAdmin || userIsEntityAdmin) && !externalSource"
                    class="gcmcn-inputs-container"
                >
                    <div class="flex-basis-10">
                        <el-form-item class="gcmcn-form-input" prop="datacenter_obj_uid">
                            <div class="gcmcn-form-label type-input-spacing">Feed resource</div>
                            <el-select
                                v-model="form.feed_resource_uid"
                                clearable
                                class="gcmcn-form-label"
                                size="mini"
                                popper-class="gcmcn-popper"
                                :disabled="disableResourceSelect"
                                @change="selectedFeedResource"
                            >
                                <el-option
                                    v-for="resource in relatedFeedResources"
                                    :key="resource.uid"
                                    :label="resource.display_name"
                                    :value="resource.uid"
                                >
                                </el-option>
                            </el-select>
                        </el-form-item>
                    </div>
                </div>

                <div class="gcmcn-inputs-container"  style="justify-content: flex-start;">
                    <div class="flex-basis-4" style="margin-right: 15px;">
                        <el-form-item class="gcmcn-form-input" prop="start_date">
                            <div class="gcmcn-form-label">Start date</div>
                            <el-date-picker
                                v-model="form.start_date"
                                popper-class="gcmcn-popper"
                                type="date"
                                size="mini"
                                placeholder="Select a date"
                                :disabled="cantEditFeed"
                                @change="handleStartDateChange"
                            >
                            </el-date-picker>
                        </el-form-item>
                    </div>
                    <div class="flex-basis-4">
                        <el-form-item class="gcmcn-form-input" prop="start_time">
                            <div class="gcmcn-form-label">At</div>
                            <ns-time-picker
                                v-model="form.start_time"
                                :disabled="cantEditFeed"
                                :display-seconds="false"
                                :display-digital-clock="true"
                            ></ns-time-picker>
                        </el-form-item>
                    </div>
                </div>
                <div class="gcmcn-inputs-container" style="justify-content: flex-start;">
                    <div class="flex-basis-4" style="margin-right: 15px;">
                        <el-form-item class="gcmcn-form-input" prop="end_date">
                            <div class="gcmcn-form-label">End date</div>
                            <el-date-picker
                                v-model="form.end_date"
                                popper-class="gcmcn-popper"
                                type="date"
                                size="mini"
                                placeholder="Select a date"
                                :disabled="cantEditFeed"
                                :pickerOptions="pickerOptions"
                                @change="handleEndDateChange"
                            >
                            </el-date-picker>
                        </el-form-item>
                    </div>
                    <div class="flex-basis-4">
                        <el-form-item class="gcmcn-form-input" prop="end_time">
                            <div class="gcmcn-form-label">At</div>
                            <ns-time-picker
                                v-model="form.end_time"
                                :disabled="cantEditFeed"
                                :display-seconds="false"
                                :display-digital-clock="true"
                            ></ns-time-picker>
                        </el-form-item>
                    </div>
                </div>
                <div class="gcmcn-inputs-container" v-if="!externalSource || (externalSource && this.source_protocol_type === 'zixi')">
                    <div class="flex-basis-10">
                        <el-form-item class="gcmcn-form-input">
                            <div class="gcmcn-form-label">Max latency (zixi)</div>
                            <el-select
                                v-model="form.latency_ms"
                                size="mini"
                                :disabled="cantEditFeed"
                                popper-class="gcmcn-popper"
                            >
                                <el-option v-for="lat in $store.getters.sortedLatencies"
                                    :key="lat.uid"
                                    :label="`${lat.value_ms} ms (${lat.name})`"
                                    :value="lat.value_ms"
                                >
                                </el-option>
                                <el-option label="/" :value="0"></el-option>
                            </el-select>
                        </el-form-item>
                    </div>
                </div>
                <div class="gcmcn-inputs-container" v-if="externalSource && this.source_protocol_type === 'srt'">
                    <div class="flex-basis-10">
                        <el-form-item class="gcmcn-form-input">
                            <div class="gcmcn-form-label">Min latency (srt)</div>
                            <el-select
                                v-model="form.latency_ms"
                                size="mini"
                                :disabled="cantEditFeed"
                                popper-class="gcmcn-popper"
                            >
                                <el-option v-for="lat in $store.getters.sortedLatencies"
                                    :key="lat.uid"
                                    :label="`${lat.value_ms} ms (${lat.name})`"
                                    :value="lat.value_ms"
                                >
                                </el-option>
                                <el-option label="/" :value="0"></el-option>
                            </el-select>
                        </el-form-item>
                    </div>
                </div>
                <el-form-item
                    class="gcmcn-form-input radio-input"
                    v-if="userIsAtLeastAdmin || canUpdateRedundancyType"
                >
                    <div class="gcmcn-form-label">Choose redundancy</div>
                    <el-radio
                        v-model="form.redundancy_type"
                        label="1+0"
                        :disabled="disabledRedundancy"
                    >
                        1+0 (MAIN only)
                    </el-radio>
                    <el-radio
                        v-model="form.redundancy_type"
                        label="1+1"
                        :disabled="disabledRedundancy"
                    >
                        1+1 (MAIN+BACKUP)
                    </el-radio>
                </el-form-item>
                <div class="gcmcn-inputs-container">
                    <div class="flex-basis-10">
                        <el-form-item class="gcmcn-form-input">
                            <div class="gcmcn-form-label" style="margin-bottom: 15px;">Description</div>
                            <el-input
                                type="textarea"
                                style="display: block;"
                                :rows="4"
                                size="mini"
                                v-model="form.description"
                                :disabled="feed !== null && feed.status === 'FINISHED'"
                            ></el-input>
                        </el-form-item>
                    </div>
                </div>
                <div class="flex-basis-10" v-if="externalSource">
                    <div class="gcmcn-inputs-container">
                        <el-form-item class="gcmcn-form-input">
                            <div class="gcmcn-form-label">Feed Type</div>
                            <el-switch
                                v-model="source_protocol_type"
                                active-text="SRT"
                                inactive-text="Zixi"
                                active-value="srt"
                                inactive-value="zixi"
                                :disabled="cantEditFeed"
                            >
                            </el-switch>
                        </el-form-item>
                    </div>
                </div>

                <div v-if="externalSource" class="gcmcn-inputs-container">
                    <div class="flex-basis-10">
                        <el-form-item class="gcmcn-form-input" prop="external_ip_cidr_main">
                            <div class="gcmcn-form-label">IP CIDR (main)</div>
                            <el-input
                                v-model="form.external_ip_cidr_main"
                                :disabled="cantEditFeed"
                                size="mini"
                            ></el-input>
                        </el-form-item>
                    </div>
                    <div class="flex-basis-10">
                        <el-form-item class="gcmcn-form-input" prop="external_ip_cidr_backup">
                            <div class="gcmcn-form-label">IP CIDR (backup)</div>
                            <el-input
                                v-model="form.external_ip_cidr_backup"
                                :disabled="cantEditFeed"
                                size="mini"
                            ></el-input>
                        </el-form-item>
                    </div>
                    <div class="flex-basis-10">
                        <el-form-item class="gcmcn-form-input">
                            <div class="gcmcn-form-label">Add email to list</div>
                            <list-email
                                ref="list-email"
                                :emailList="emails"
                                @on-add-email="onAddEmail"
                                @remove-email-from-list="removeEmailFromList"
                            ></list-email>
                        </el-form-item>
                    </div>
                </div>
                <div class="text-right">
                    <el-button
                        type="text"
                        @click="leave"
                    >Cancel</el-button>
                    <el-button
                        :loading="loading"
                        type="primary"
                        class="gcmcn-primary-btn small"
                        :disabled="feed !== null && feed.status === 'FINISHED'"
                        @click="validateForm">Save</el-button>
                </div>
            </el-form>
            <cropper-modal
                ref="cropper"
                @on-image-cropped="onImageCropped"
                title="Configure thumbnail"
            >
                <ul class="crop-rules">
                    <li>Image formats allowed are jpeg, jpg and png.</li>
                    <li>Best ratio 16:9</li>
                    <li>Minimal image size allowed is 400 x 225.</li>
                </ul>
            </cropper-modal>
        </div>
    </div>
</template>
</template>

<script>

import {
    CropperModal,
    UploadFile,
} from 'gcmcn-common';
import { mapGetters } from 'vuex';
import moment from 'moment';
import NsTimePicker from 'fundation-app-vuejs/src/components/partials/ns-time-picker.vue';
import ListEmail from '../../partials/ListEmailValidatorVue';

export default {

    components: {
        CropperModal,
        UploadFile,
        NsTimePicker,
        ListEmail,
    },

    props: {
        universes: {
            type: Array,
            required: true,
        },
        events: {
            type: Array,
            required: true,
        },
        feeds: {
            type: Array,
            required: true,
        },
        feedResources: {
            type: Array,
            required: true,
        },
        feed: {
            required: true,
            validator: prop => typeof prop === 'object' || prop === null
        },
    },

    data (){
        return {
            emails: [],
            externalSource: false,
            loading: false,
            form: {},
            defaultImage: '',
            formInitialised: false,
            source_protocol_type: 'zixi',
            disabledRedundancy: false

        };
    },

    computed: {
        ...mapGetters([
            'entitiesByUid',
            'administrableEntitiesUids',
            'userIsAtLeastAdmin',
            'userIsEntityAdmin'
        ]),
        entityUid(){
            if(this.form.event_uid){
                return this.events.find((ev) => ev.uid === this.form.event_uid).scopes.entity_uid;
            }
            return null;
        },
        rules (){
            let rules = {
                display_name: [{ required: true, validator: this.authorizedCharacters }],
                thumbnail: [{ required: true }],
                event_uid: [{ required: true }],
                start_date: [{required: true, message: 'Start date is required' }],
                end_date: [{ validator: this.validEndDate, trigger: 'blur' }],
                start_time: [{ validator: this.validStartTime }],
                end_time: [{ validator: this.validEndTime }],
            };
            if(this.externalSource){
                rules.external_ip_cidr_main = [{ required: true, validator: this.checkIp }];
                rules.external_ip_cidr_backup = [{ required: true, validator: this.checkIp }];
            }
            return rules;
        },
        availableEvents (){
            if(!this.feed) return this.events;
            return this.events.filter((ev) => ev.scopes.entity_uid === this.feed.scopes.entity_uid);
        },
        canUpdateRedundancyType(){
            if(this.entityUid){
                return this.userIsEntityAdmin(this.entityUid);
            }
            return false;
        },
        cantEditFeed (){
            if(this.feed !== null && this.feed.confirmed){
                return true;
            }
            return this.feed !== null && this.feed.status === 'FINISHED';
        },

        pickerOptions (){
            return {
                disabledDate: (time) => {
                    // We use new Date with moment because we need to handle utc date with moment and el-date-picker only handle Date objects
                    let startDate = moment(this.form.start_date).format('YYYY-MM-DD');
                    return time.getTime() < new Date(moment(startDate).utc());
                },
            };
        },
        relatedFeedResources(){
            if(this.form.event_uid !== ''){
                let relatedEvent = this.events.find((event) => event.uid === this.form.event_uid);
                return this.feedResources
                    .filter(
                        (resource) => resource.universe_uid === relatedEvent.universe_uid
                        && resource.status !== 'FINISHED'
                    );
            }
        },
        disableResourceSelect(){
            if(this.feed !== null){
                return true;
            } else if(this.userIsEntityAdmin(this.entityUid) || this.userIsAtLeastAdmin) {
                return this.form.event_uid === '';
            }
            return true;
        },
        nameAlreadyExist(){
            if(this.form.event_uid && this.form.display_name){
                let nameToCheck = this.form.display_name.toLowerCase();
                let relatedFeeds = this.feeds.filter(
                    feed => feed.event_uid === this.form.event_uid && feed.status !== 'FINISHED'
                )
                return relatedFeeds.some(feed => feed.name.toLowerCase() === nameToCheck)
            }
            return false;
        }
    },

    watch: {
        rules: {
            handler (){
                this.$nextTick( () => {
                    this.$refs.form.clearValidate();
                });
            },
            deep: true,
        },
    },

    mounted (){
        this.setForm();
    },
    methods: {
        handleStartDateChange(date){
            this.form.start_date = new Date(moment(date).format('YYYY-MM-DD'));
        },
        handleEndDateChange(date){
            this.form.end_date = new Date(moment(date).format('YYYY-MM-DD'));
        },
        selectedFeedResource(){
            if(this.form.feed_resource_uid !== ''){
                let selectedResource = this.relatedFeedResources
                    .find(resource => resource.uid === this.form.feed_resource_uid);
                this.$set(this.form, 'redundancy_type', selectedResource.redundancy_type);
                this.disabledRedundancy = true;
            } else {
                let uid = this.events.find((ev) => ev.uid === this.form.event_uid).scopes.entity_uid;
                let entity = this.entitiesByUid(uid);
                this.form.redundancy_type = entity.feed_redundancy_type;
                this.disabledRedundancy = false;
            }

        },
        eventChanged(){
            this.$set(this.form, 'feed_resource_uid', '');
            let uid = this.events.find((ev) => ev.uid === this.form.event_uid).scopes.entity_uid;
            let entity = this.entitiesByUid(uid);
            this.$set(this.form, 'redundancy_type', entity.feed_redundancy_type);
            this.disabledRedundancy = false;
        },
        removeEmailFromList (email){
            let index = this.emails.indexOf(email);
            if(index > -1){
                this.emails.splice(index, 1);
            }
        },
        onAddEmail (email){
            this.emails.push(email);
        },

        checkIp (rule, value, callback) {
            if(!value){
                return callback(new Error('IP CIDR is required'));
            }
            if(!value.match(/^(([0-9]|[1-9][0-9]|1[0-9]{2}|2[0-4][0-9]|25[0-5]).){3}([0-9]|[1-9][0-9]|1[0-9]{2}|2[0-4][0-9]|25[0-5])\/([0-9]|[1-2][0-9]|3[0-2])$/)){
                return callback(new Error('IP CIDR must be a valid ip V4 with mask'));
            }
            return callback();
        },
        validStartTime (rule, value, callback) {
            if(!this.form.start_time){
                return callback(new Error('Start time is required'));
            }else if(!this.form.start_time.match(/^([0-1][0-9]|2[0-3]|[0-9])\:([0-5][0-9]|[1-9])$/gm)){
                return callback(new Error('Start time format must match HH:mm pattern'));
            }
            callback();
        },
        validEndTime (rule, value, callback) {
            if(!this.form.end_time){
                return callback(new Error('End time is required'));
            }else if(!this.form.end_time.match(/^([0-1][0-9]|2[0-3]|[0-9])\:([0-5][0-9]|[1-9])$/gm)){
                return callback(new Error('End time format must match HH:mm pattern'));
            }
            let timeStartInt = parseInt(this.form.start_time.replace(/\:/g, ''));
            let timeEndInt = parseInt(this.form.end_time.replace(/\:/g, ''));
            let startTimeIsBefore = new Date(this.form.start_date) < new Date(this.form.end_date);
            if(!startTimeIsBefore && this.form.start_time !== '' && timeStartInt >= timeEndInt){
                return callback(new Error('End time musn\'t be before start time'));
            }
            callback();
        },
        validEndDate (rule, value, callback) {
            if(!this.form.end_date){
                return callback(new Error('End date is required'));
            }
            let startDateIsBefore = new Date(this.form.start_date).getTime() <= new Date(this.form.end_date).getTime();
            if(!startDateIsBefore){
                return callback(new Error('End date must be after start date'));
            }
            callback();
        },
        authorizedCharacters(rule, value, callback) {
            if(!this.form.display_name) {
                return callback(new Error('Name is required'));
            } else if (!this.form.display_name.match(this.$store.getters.authorizedCharactersRegex)) {
                return callback(new Error('Invalid name'));
            } else if(this.nameAlreadyExist && !this.feed) {
                // check only on creation
                callback(new Error('Feed name already exists.'));
            } else {
                callback();
            }
        },
        setForm (){
            if(this.feed){
                if (this.feed.feed_resource_uid){
                    this.disabledRedundancy = true;
                }
                this.$set(this, 'form', JSON.parse(JSON.stringify(this.feed)));
                this.form.start_time = moment.utc(this.feed.confirmed ? this.form.confirmed_start_date : this.form.start_date).format('HH:mm');
                this.form.end_time = moment.utc(this.feed.confirmed ? this.form.confirmed_end_date : this.form.end_date).format('HH:mm');
                // We use new Date with moment because we need to handle utc date with moment and el-date-picker only handle Date objects
                this.$set(this.form, 'start_date', new Date(moment(this.feed.confirmed ? this.form.confirmed_start_date : this.form.start_date).utc().format('YYYY-MM-DD')));
                this.$set(this.form, 'end_date', new Date(moment(this.feed.confirmed ? this.form.confirmed_end_date : this.form.end_date).utc().format('YYYY-MM-DD')));
                this.$set(this, 'emails', this.feed.source_emails.constructor === Object ? [] : this.feed.source_emails);
                this.externalSource = this.feed.source_type !== 'internal';
                this.source_protocol_type = this.feed.source_protocol_type;
                let latency_ms = 0;
                if (this.source_protocol_type === 'zixi'){
                    latency_ms = this.feed.max_latency_ms
                }
                else{
                    latency_ms = this.feed.min_latency_ms
                }
                this.$set(this.form, 'latency_ms', latency_ms)

            }else{
                this.$set(this, 'form', {
                    confirmed: false,
                    name: '',
                    display_name: '',
                    thumbnail: '',
                    event_uid: '',
                    feed_resource_uid: '',
                    start_date: '',
                    start_time: '',
                    end_date: '',
                    end_time: '',
                    description: '',
                    latency_ms: 0,
                    external_ip_cidr_main: '',
                    external_ip_cidr_backup: '',
                    booking_key:'',
                    redundancy_type: '1+1'
                });
                this.$set(this, 'emails', []);
            }
            this.formInitialised = true;
        },
        onCropImage (data){
            this.$refs.cropper.open(data);
        },
        async onImageCropped (croppedImage){
            const url = croppedImage;
            this.defaultImage = url;
            const file = await this.$store.dispatch('createBlobFromUrl', {imageUrl: url});
            this.form.thumbnail = file;
        },
        validateAllEmails (){
            if(this.externalSource){
                return this.$refs['list-email'].validateAllEmails();
            }
            return true;
        },
        validateForm (){
            this.$refs.form.validate((valid) => {
                if(valid && this.validateAllEmails()){
                    this.loading = true;
                    let form = JSON.parse(JSON.stringify(this.form));
                    form.start_time = moment.utc(this.form.start_time, 'HH:mm:ss').format('HH:mm:ss');
                    form.end_time = moment.utc(this.form.end_time, 'HH:mm:ss').format('HH:mm:ss');
                    let startTimeSplitted = form.start_time.split(':');
                    let endTimeSplitted = form.end_time.split(':');
                    form.start_date = moment(form.start_date).set({
                        hours: startTimeSplitted[0],
                        minutes: startTimeSplitted[1],
                        seconds: startTimeSplitted[2],
                    }).format('YYYY-MM-DDTHH:mm:ss');
                    form.end_date = moment(form.end_date).set({
                        hours: endTimeSplitted[0],
                        minutes: endTimeSplitted[1],
                        seconds: endTimeSplitted[2],
                    }).format('YYYY-MM-DDTHH:mm:ss');
                    delete form.thumbnail;
                    let action;
                    let postData = { item: form, model: 'feeds'};
                    let messageType = this.feed ? 'update' : 'create';
                    if(this.feed){
                        action = 'patchItem';
                        this.form.url = this.feed.url;
                    }else{
                        let scope = this.events.find((ev) => ev.uid === this.form.event_uid).scopes;
                        form.name = form.display_name;
                        action = 'createItem';
                        form.can_admin_users = [this.$store.getters.currentUser.uid];
                        postData.url = this.$store.state.api.meta.listModels.feeds.url;
                        postData.scopes = {'X-Entity-Uid': scope.entity_uid};
                    }
                    if(this.externalSource){
                        postData.item.source_type = 'external';
                        postData.item.source_protocol_type = this.source_protocol_type;
                        if (this.source_protocol_type === 'zixi'){
                            postData.item.max_latency_ms = form.latency_ms
                        }
                        else{
                            postData.item.min_latency_ms = form.latency_ms
                        }
                        postData.item.source_emails = this.emails;
                    }else{
                        postData.item.source_type = 'internal';
                        postData.item.max_latency_ms = form.latency_ms
                        delete postData.item.min_latency_ms;
                        delete postData.item.external_ip_cidr_main;
                        delete postData.item.external_ip_cidr_backup;
                        delete postData.item.feed_source_type;
                    }
                    this.$store.dispatch(action, postData).then((res) => {
                        this.$message({
                            message: `Feed ${messageType}d!`,
                            type: 'success'
                        });
                        this.show = false;
                        if(this.form.thumbnail){
                            // si ke thumbnail est un string c'est qu'il s'agit de l'url du fichier créer par le server, donc que le thumbnail n' pas été modifié
                            if(this.feed && typeof(this.form.thumbnail) === 'string') {
                                this.$emit('feed-created');
                                return;
                            }
                            let fd = new FormData();
                            fd.append('thumbnail', this.form.thumbnail);
                            this.$store.dispatch('patchItemFormData', {
                                item: fd,
                                url: res.url,
                                model: 'feeds',
                            }).then(() => { this.$emit('feed-created'); }).catch((e) => {
                                console.error(e);
                                this.$message.error(`Error on feed ${messageType === 'update' ? messageType : 'creation'}.`);
                                this.loading = false;
                            });
                        }else{
                            this.$emit('feed-created');
                            this.loading = false;
                        }
                    }).catch((e) => {
                        console.error(e);
                        this.$message.error(`Error on feed ${messageType === 'update' ? messageType : 'creation'}.`);
                        this.loading = false;
                    });
                }
            });
        },
        leave (){
            this.$emit('leave-form-view');
        },
    },
};
</script>

<style lang="scss">
.form-feed{
    .el-switch__label:not(.is-active){
        color: #858d96;
    }
    .crop-rules{
        text-transform: initial;
        color: #bbb;

    }
    .gcmcn-danger-btn{
        margin-top: 20px;
        margin-bottom: 20px;
    }

}
</style>



// WEBPACK FOOTER //
// src/components/page/Feeds/feed-form.vue